import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { css, useTheme } from 'styled-components';
import styled from 'styled-components';

import { ORDER_TABLE_TABS } from 'src/common/constants';
import { Checkbox } from 'src/components/core';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { Tab, Tabs } from 'src/components/core/tabs';
import { useAuth } from 'src/context/AuthContext';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import {
    fetchPositions,
    fetchActiveOrders,
    fetchConditionalOrders,
    fetchTradeOrders,
    fetchMyTrades,
} from 'src/setup/redux/slices/derivativesSlice';
import { ActiveOrder, ConditionalOrder } from 'src/types';

import { ActiveOrdersTable } from './desktop/activeOrdersTable';
import { ClosePLTable } from './desktop/closePLTable';
import { ConditionalOrdersTable } from './desktop/conditionalOrdersTable';
import { FilledOrdersTable } from './desktop/filledOrderTable';
import { OrderHistoryTable } from './desktop/orderHistoryTable';
import { PositionTable } from './desktop/positionTable';
import { ActiveOrdersCard } from './mobile/activeOrdersCard';
import { ClosePLCard } from './mobile/closePLCard';
import { ConditionalOrdersCard } from './mobile/conditionalOrdersCard';
import { FilledOrdersCard } from './mobile/filledOrderCard';
import { OrderHistoryCard } from './mobile/orderHistoryCard';
import { PositionCard } from './mobile/positionCard';
import { ErrorBoundary } from '../../common/error/ErrorBoundary';

export const OrderTableContainer = styled.div<{ isExpand: boolean }>`
    ${(props) =>
        props.isExpand
            ? css`
                  display: none;
              `
            : css`
                  display: block;
              `}
    ${({ theme: { colors, typography, breakpoints } }) => `
    width: 100%;
    .order-tabs-header {
        -ms-overflow-style: none;
        scrollbar-width: none;
    }
    .order-tabs-header::-webkit-scrollbar {
        display: none;
    }
    .tabs {
        .tab {
            min-width: max-content;
            font-size: ${typography.size.desktop.paragraph.p3};
            font-weight: ${typography.weight.normal};
            line-height: calc(${typography.lineHeight[2]} - 1px);
            padding: 12px 12px;
            &.tab-active {
                font-weight: ${typography.weight.medium};
                padding: 12px 12px 10px;
            }
        }
    }
    @media (max-width: ${breakpoints.md}) {
        .tabs {
            & > .tab:not(:last-of-type) {
                margin-right: 32px;
            }
            .tab {
                font-size: ${typography.size.desktop.paragraph.p2};
                font-weight: ${typography.weight.normal};
                line-height: ${typography.lineHeight[4]};
                padding: 10px 0px;
                &.tab-active {
                    padding: 10px 0px 8px;
                }
            }
        }
    }
    & .no-record {
        & p {
            font-size: ${typography.size.desktop.paragraph.p3};
            font-weight: ${typography.weight.normal};
            line-height: calc(${typography.lineHeight[2]} - 1px);
            color: ${colors.text.tertiary};
        }
    }
    & .logout-user-message {
        & p {
            font-size: ${typography.size.desktop.paragraph.p3};
            font-weight: ${typography.weight.normal};
            line-height: calc(${typography.lineHeight[2]} - 1px);
            color: ${colors.text.tertiary};
        }
    }
    & .show-all-orders {
        min-width: max-content;
        margin: 0 24px;
        & label {
            color: ${colors.text.tertiary};
            font-size: ${typography.size.desktop.paragraph.p3};
            font-weight: ${typography.weight.normal};
            line-height: calc(${typography.lineHeight[2]} - 1px);
            @media (max-width: ${breakpoints.md}) {
                font-size: ${typography.size.desktop.paragraph.p2};
                font-weight: ${typography.weight.normal};
                line-height: ${typography.lineHeight[4]};
            }
        }
    }
    `}
`;

export const StyledCheckbox = styled(Checkbox)<{ isChecked: boolean }>`
    ${(props) =>
        !props.isChecked &&
        css`
            ${({ theme: { colors } }) => `
        :before {
            border: 1px solid ${colors.text.tertiary};
            background-color: ${colors.background.secondary.v3};
        }
        `}
        `}
`;
interface OrderTableProps {
    isExpand: boolean;
}
const OrderTable: FC<OrderTableProps> = ({ isExpand }) => {
    const { t } = useTranslation();
    const { isMobile, colors } = useTheme();
    const [activeTab, setActiveTab] = useState(0);
    const [activeOrderCount, setActiveOrderCount] = useState(0);
    const [conditionalOrderCount, setConditionalOrderCount] = useState(0);
    const [isShowAllPosition, setIsShowAllPosition] = useState(true);
    const [isShowAllActiveOrder, setIsShowAllActiveOrder] = useState(true);
    const [isShowAllConditionalOrder, setIsShowAllConditionalOrder] = useState(true);
    const [isShowAllClosePnl, setIsShowAllClosePnl] = useState(true);
    const [isShowAllFilledOrders, setIsShowAllFilledOrders] = useState(true);
    const [isShowAllOrderHistory, setIsShowAllOrderHistory] = useState(true);

    const { error, loading } = useAppSelector((state) => state.derivatives);

    const symbolId = useAppSelector((state) => state.derivatives.currentSymbol.symbolId);

    const activeOrders: Array<ActiveOrder> = useAppSelector(
        (state) => state.derivatives.activeOrders,
    );
    const conditionalOrders: Array<ConditionalOrder> = useAppSelector(
        (state) => state.derivatives.conditionalOrders,
    );
    const { userCurrentFutureTradeAccountIndex } = useAppSelector((state) => state.global);

    const fetchPositionsError = error?.fetchPositions !== undefined;
    const fetchPositionsLoading = loading?.fetchPositions;
    const fetchActiveOrdersError = error?.fetchActiveOrders !== undefined;
    const fetchActiveOrdersLoading = loading?.fetchActiveOrders;
    const fetchConditionalOrdersError = error?.fetchConditionalOrders !== undefined;
    const fetchConditionalOrdersLoading = loading?.fetchConditionalOrders;
    const fetchTradeOrdersError = error?.fetchTradeOrders !== undefined;
    const fetchTradeOrdersLoading = loading?.fetchTradeOrders;
    const fetchMyTradesError = error?.fetchMyTrades !== undefined;
    const fetchMyTradesLoading = loading?.fetchMyTrades;

    const dispatch = useAppDispatch();
    const { isAuthenticated } = useAuth();

    const fetchDerivativesPositions = () => {
        dispatch(fetchPositions({ account_index: userCurrentFutureTradeAccountIndex }));
    };
    const fetchDerivativesActiveOrders = () => {
        dispatch(
            fetchActiveOrders(
                `checked=false&order_type=LIMIT&firstReq=true&type=LIMIT&limit=100&from_order_id=&account_index=${userCurrentFutureTradeAccountIndex}`,
            ),
        );
    };
    const fetchDerivativesConditionalOrders = () => {
        dispatch(
            fetchConditionalOrders(
                `checked=false&order_type=STOP&firstReq=true&type=STOP&limit=100&from_order_id=&account_index=${userCurrentFutureTradeAccountIndex}`,
            ),
        );
    };
    const fetchDerivativesTradeOrders = () => {
        dispatch(
            fetchTradeOrders(
                `checked=false&order_type=&firstReq=true&limit=50&from_order_id=&type=&account_index=${userCurrentFutureTradeAccountIndex}`,
            ),
        );
    };
    const fetchDerivativesMyTrades = () => {
        dispatch(
            fetchMyTrades(
                `checked=false&order_type=LIMIT&firstReq=true&function=historyOrder&api=futures_history_order&limit=50&from_trade_id=&account_index=${userCurrentFutureTradeAccountIndex}`,
            ),
        );
    };

    // TODO: Need to improve the api calling for individual tabs.
    useEffect(() => {
        // TODO: Fix according to design when user is login then should only call the api else render the logout user design
        if (isAuthenticated && userCurrentFutureTradeAccountIndex !== undefined) {
            fetchDerivativesPositions();
            // TODO: need to handle active and conditional order through single method.
            fetchDerivativesActiveOrders();
            // TODO: need to handle active and conditional order through single method.
            fetchDerivativesConditionalOrders();
            fetchDerivativesTradeOrders();
            fetchDerivativesMyTrades();
        }
    }, [dispatch, isAuthenticated, userCurrentFutureTradeAccountIndex]);

    // TODO: Need to improve the api calling for individual tabs.
    useEffect(() => {
        // TODO: Fix according to design when user is login then should only call the api else render the logout user design
        if (
            isAuthenticated &&
            userCurrentFutureTradeAccountIndex !== undefined &&
            (orderTableTabs[activeTab].label === ORDER_TABLE_TABS.FILLED ||
                orderTableTabs[activeTab].label === ORDER_TABLE_TABS.ORDER_HISTORY)
        ) {
            fetchDerivativesTradeOrders();
        }
        if (isAuthenticated && orderTableTabs[activeTab].label === ORDER_TABLE_TABS.CLOSED_PNL) {
            fetchDerivativesMyTrades();
        }
    }, [dispatch, activeTab, userCurrentFutureTradeAccountIndex]);

    const getActiveOrderCount = useCallback(() => {
        const filteredActiveOrders = isShowAllActiveOrder
            ? activeOrders
            : activeOrders.filter((tradeOrder) => tradeOrder.symbolId === symbolId);
        return filteredActiveOrders.length;
    }, [activeOrders, isShowAllActiveOrder, symbolId]);

    const getConditionalOrderCount = useCallback(() => {
        const filteredConditionalOrders = isShowAllConditionalOrder
            ? conditionalOrders
            : conditionalOrders?.filter(
                  (conditionalOrder) => conditionalOrder.symbolId === symbolId,
              );
        return filteredConditionalOrders?.length ?? 0;
    }, [conditionalOrders, isShowAllConditionalOrder, symbolId]);

    useEffect(() => {
        setActiveOrderCount(getActiveOrderCount());
        setConditionalOrderCount(getConditionalOrderCount());
    }, [
        getActiveOrderCount,
        getConditionalOrderCount,
        setActiveOrderCount,
        setConditionalOrderCount,
    ]);

    const orderTableTabs = [
        {
            label: ORDER_TABLE_TABS.POSITIONS,
            desktopContent: <PositionTable isShowAll={isShowAllPosition} />,
            mobileContent: <PositionCard isShowAll={isShowAllPosition} />,
            showAllCheckbox: (
                <>
                    <ErrorBoundary
                        hasError={fetchPositionsError}
                        loading={fetchPositionsLoading}
                        retryAction={fetchDerivativesPositions}
                    />
                    <StyledCheckbox
                        value={isShowAllPosition}
                        isDefault={true}
                        label={t('show_all_positions')}
                        onChange={setIsShowAllPosition}
                        isChecked={isShowAllPosition}
                    />
                </>
            ),
        },
        {
            label: ORDER_TABLE_TABS.CLOSED_PNL,
            desktopContent: <ClosePLTable isShowAll={isShowAllClosePnl} />,
            mobileContent: <ClosePLCard isShowAll={isShowAllClosePnl} />,
            showAllCheckbox: (
                <>
                    <ErrorBoundary
                        hasError={fetchMyTradesError}
                        loading={fetchMyTradesLoading}
                        retryAction={fetchDerivativesMyTrades}
                    />
                    <StyledCheckbox
                        value={isShowAllClosePnl}
                        isDefault={true}
                        label={t('all_close_pnl')}
                        onChange={setIsShowAllClosePnl}
                        isChecked={isShowAllClosePnl}
                    />
                </>
            ),
        },
        {
            label: ORDER_TABLE_TABS.ACTIVE_ORDERS,
            icon: (
                <span style={{ color: colors.primary, marginLeft: '5px' }}>{activeOrderCount}</span>
            ),
            iconPlacement: 'right',
            desktopContent: <ActiveOrdersTable isShowAll={isShowAllActiveOrder} />,
            mobileContent: <ActiveOrdersCard isShowAll={isShowAllActiveOrder} />,
            showAllCheckbox: (
                <>
                    <ErrorBoundary
                        hasError={fetchActiveOrdersError}
                        loading={fetchActiveOrdersLoading}
                        retryAction={fetchDerivativesActiveOrders}
                    />
                    <StyledCheckbox
                        value={isShowAllActiveOrder}
                        isDefault={true}
                        label={t('all_active_orders')}
                        onChange={setIsShowAllActiveOrder}
                        isChecked={isShowAllActiveOrder}
                    />
                </>
            ),
        },
        {
            label: ORDER_TABLE_TABS.CONDITIONAL_ORDERS,
            icon: (
                <span style={{ color: colors.primary, marginLeft: '5px' }}>
                    {conditionalOrderCount}
                </span>
            ),
            desktopContent: <ConditionalOrdersTable isShowAll={isShowAllConditionalOrder} />,
            mobileContent: <ConditionalOrdersCard isShowAll={isShowAllConditionalOrder} />,
            showAllCheckbox: (
                <>
                    <ErrorBoundary
                        hasError={fetchConditionalOrdersError}
                        loading={fetchConditionalOrdersLoading}
                        retryAction={fetchDerivativesConditionalOrders}
                    />
                    <StyledCheckbox
                        value={isShowAllConditionalOrder}
                        isDefault={true}
                        label={t('all_conditional_orders')}
                        onChange={setIsShowAllConditionalOrder}
                        isChecked={isShowAllConditionalOrder}
                    />
                </>
            ),
        },
        {
            label: ORDER_TABLE_TABS.FILLED,
            desktopContent: <FilledOrdersTable isShowAll={isShowAllFilledOrders} />,
            mobileContent: <FilledOrdersCard isShowAll={isShowAllFilledOrders} />,
            showAllCheckbox: (
                <>
                    <ErrorBoundary
                        hasError={fetchTradeOrdersError}
                        loading={fetchTradeOrdersLoading}
                        retryAction={fetchDerivativesTradeOrders}
                    />
                    <StyledCheckbox
                        value={isShowAllFilledOrders}
                        isDefault={true}
                        label={t('all_filled_orders')}
                        onChange={setIsShowAllFilledOrders}
                        isChecked={isShowAllFilledOrders}
                    />
                </>
            ),
        },
        {
            label: ORDER_TABLE_TABS.ORDER_HISTORY,
            desktopContent: <OrderHistoryTable isShowAll={isShowAllOrderHistory} />,
            mobileContent: <OrderHistoryCard isShowAll={isShowAllOrderHistory} />,
            showAllCheckbox: (
                <>
                    <ErrorBoundary
                        hasError={fetchTradeOrdersError}
                        loading={fetchTradeOrdersLoading}
                        retryAction={fetchDerivativesTradeOrders}
                    />
                    <StyledCheckbox
                        value={isShowAllOrderHistory}
                        isDefault={true}
                        label={t('all_order_history')}
                        onChange={setIsShowAllOrderHistory}
                        isChecked={isShowAllOrderHistory}
                    />
                </>
            ),
        },
    ];
    return (
        <OrderTableContainer isExpand={isExpand} className="order-table">
            <>
                <FlexContainer
                    className="order-tabs-header"
                    styles={{
                        background: `${colors.background.secondary.v3}`,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderTop: `1px solid ${colors.border.light}`,
                        height: '39px',
                        width: '100%',
                        overflowX: 'scroll',
                        overflowY: 'hidden',
                    }}
                >
                    <Tabs value={activeTab} onChange={setActiveTab} className={'order-table-tabs'}>
                        {orderTableTabs.map((tab, index) => {
                            return (
                                <Tab
                                    value={index}
                                    key={index}
                                    label={t(tab.label)}
                                    icon={tab.icon}
                                    iconPlacement={'right'}
                                    className={tab.label === 'positions' ? 'Position' : ''}
                                />
                            );
                        })}
                    </Tabs>
                    {orderTableTabs[activeTab].showAllCheckbox && (
                        <FlexContainer className="show-all-orders">
                            {orderTableTabs[activeTab].showAllCheckbox}
                        </FlexContainer>
                    )}
                </FlexContainer>
                <FlexContainer
                    styles={{
                        alignItems: 'center',
                        padding: isMobile ? '0 16px' : '0 0 0 20px',
                        width: '100%',
                        minHeight: isMobile ? '210px' : '300px',
                        background: `${colors.background.primary}`,
                    }}
                >
                    {isMobile
                        ? orderTableTabs[activeTab].mobileContent
                        : orderTableTabs[activeTab].desktopContent}
                </FlexContainer>
            </>
        </OrderTableContainer>
    );
};

export { OrderTable };
